/**
 * 图块画面的**五格 + 限定自由口**(2026-08-02,条 3)。
 *
 * ═══ 为什么不是一个「画面描述」文本框 ═══════════════════════════════════════
 * 人眼比对 60 个真任务的 42 条图块 prompt:**57% 根本不是画面描述,是营销文案**
 * (`derivePropsFor` 把 title+subtitle+bullets 拼起来当 prompt)。
 * 剩下 42% 真描述里,反复溢出的只有一类 —— 留白位置。
 *
 * 于是拍板五格,而且**三个是选择题**:
 *  · 取景 = `shotType`(9) + `angle`(7)
 *  · 光影 = `lighting`(4) + `material`(5) + `tone`(5)
 *  · 留白 = `negativeSpace`(6,**现成枚举**,顺带接上「预留区与落位互不知情」那条老缺陷)
 * ★三个选择题全部复用已有枚举,一个新值都没造。比设计稿原本画的四个自由文本框
 *   更服务小白:他不用自己想词,而我们也不用再猜他那句话是什么意思。
 *
 * ═══ 自由口为什么只许说「画面里还要有什么」 ═══════════════════════════════
 * E4② 查出的 11% 违反**全部是属性打架**(DNA 说金属质感 vs 描述说哑光),不是内容重复。
 * 属性各占一槽 → 矛盾源从结构上关掉;而「杯口点缀干桂花」这类**内容**既不与任何 DNA
 * 冲突,又正是表达力所在。**关的是属性口,不是内容口。**
 */
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import type { AssetAngle, AssetNegativeSpace, AssetShotType, ImageSlots } from "../model/canvas";
import type { Lighting, Material, StyleTone } from "../model/canvas";
import {
  ANGLE_WORDS,
  LIGHTING_WORDS,
  MATERIAL_WORDS,
  NEGATIVE_SPACE_WORDS,
  SHOT_WORDS,
  TONE_WORDS,
} from "../model/slotWords";

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-1.5 text-2xs font-medium text-muted-foreground">{children}</div>
);

/**
 * 一个下拉。**「不指定」的值是 `null` 不是空串** ——
 * 空串会被 ai 侧当成一个自造枚举值拒收,而那时用户只会看到一句"出图失败"。
 */
function SlotSelect<T extends string>({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: T | null | undefined;
  options: Array<[T, string]>;
  onPick: (v: T | null) => void;
}) {
  return (
    <label className="min-w-0 flex-1">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(e) => onPick((e.target.value || null) as T | null)}
        className={cn(
          "h-8 w-full rounded-md border border-border/60 bg-card/40 px-2 text-xs",
          "focus:outline-none focus:ring-1 focus:ring-primary/40"
        )}
      >
        <option value="">{label} · 不指定</option>
        {options.map(([v, w]) => (
          <option key={v} value={v}>
            {w}
          </option>
        ))}
      </select>
    </label>
  );
}

/** 枚举表 → 下拉选项;`unknown`/`none` 这种"没有"值不进选项(「不指定」已经代表它)。 */
function optionsOf<T extends string>(words: Record<T, string>): Array<[T, string]> {
  return (Object.entries(words) as Array<[T, string]>).filter(([, w]) => w.trim().length > 0);
}

interface Props {
  slots: ImageSlots;
  /** 只 patch 改动的那一格 —— 整包回写会把用户刚在别的格里敲的字冲掉。 */
  onChange: (patch: Partial<ImageSlots>) => void;
  /**
   * ★藏掉主体格(2026-08-05,D3):一段话主控绑的就是 `subject`,精调区再摆一个「主体」
   * 输入框,等于同一个字段在同一屏出现两次 —— 用户会以为是两个东西,改哪个都疑神疑鬼。
   * (这条是单测抓出来的:`getByDisplayValue` 报 multiple elements found。)
   */
  hideSubject?: boolean;
}

export function SlotFields({ slots, onChange, hideSubject }: Props) {
  return (
    <div className="space-y-3" data-longimage-slots>
      {/* ★hideSubject 时**不渲染**而不是 hidden:留在 DOM 里的输入框照样能被
          辅助技术读到、也照样是「同一个值出现两次」—— 藏起来只是眼不见为净。 */}
      {!hideSubject && (
        <div data-slot="subject">
          <FieldLabel>主体 · 画面里最主要的是什么</FieldLabel>
          <Input
            aria-label="主体"
            value={slots.subject ?? ""}
            maxLength={120}
            placeholder="例:一瓶 300ml 冷压鲜橙汁"
            onChange={(e) => onChange({ subject: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
      )}

      <div data-slot="framing">
        <FieldLabel>取景 · 怎么拍这个主体</FieldLabel>
        <div className="flex gap-2">
          <SlotSelect<AssetShotType>
            label="镜头"
            value={slots.shotType}
            options={optionsOf(SHOT_WORDS)}
            onPick={(v) => onChange({ shotType: v })}
          />
          <SlotSelect<AssetAngle>
            label="角度"
            value={slots.angle}
            options={optionsOf(ANGLE_WORDS)}
            onPick={(v) => onChange({ angle: v })}
          />
        </div>
      </div>

      <div data-slot="environment">
        <FieldLabel>环境 · 主体放在什么地方</FieldLabel>
        <Input
          aria-label="环境"
          value={slots.environment ?? ""}
          maxLength={120}
          placeholder="例:白色大理石台面"
          onChange={(e) => onChange({ environment: e.target.value })}
          className="h-8 text-xs"
        />
      </div>

      <div data-slot="light">
        <FieldLabel>光影 · 光线与质感</FieldLabel>
        <div className="flex flex-wrap gap-2">
          <SlotSelect<Lighting>
            label="布光"
            value={slots.lighting}
            options={optionsOf(LIGHTING_WORDS)}
            onPick={(v) => onChange({ lighting: v })}
          />
          <SlotSelect<Material>
            label="材质"
            value={slots.material}
            options={optionsOf(MATERIAL_WORDS)}
            onPick={(v) => onChange({ material: v })}
          />
          <SlotSelect<StyleTone>
            label="调性"
            value={slots.tone}
            options={optionsOf(TONE_WORDS)}
            onPick={(v) => onChange({ tone: v })}
          />
        </div>
      </div>

      <div data-slot="negativeSpace">
        <FieldLabel>留白 · 哪一片留空给文字</FieldLabel>
        <SlotSelect<AssetNegativeSpace>
          label="留白位置"
          value={slots.negativeSpace}
          options={optionsOf(NEGATIVE_SPACE_WORDS)}
          onPick={(v) => onChange({ negativeSpace: v })}
        />
      </div>

      <div data-slot="extra">
        <FieldLabel>画面里还要有什么(可空)</FieldLabel>
        <Input
          aria-label="画面里还要有什么"
          value={slots.extra ?? ""}
          maxLength={120}
          placeholder="例:杯口点缀两片干橙"
          onChange={(e) => onChange({ extra: e.target.value })}
          className="h-8 text-xs"
        />
        {/* ★把限定写在界面上,不只写在提示词里 —— 用户看不见的约束等于没有约束。 */}
        <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
          只说画面里还该出现什么东西。
          <b className="text-foreground">不用说光线、材质、风格、调性</b>
          ——上面各有一格,说两遍反而会打架。
        </p>
      </div>
    </div>
  );
}
